/**
 * Chat Listen playback — deliberate pauses, not accidental ones.
 *
 * WHY THIS EXISTS (Founder listening test, June 2026):
 * gpt-4o-mini-tts adds its own leading/trailing silence to every clip, so sequential <audio>
 * playback produced seam gaps measured at 79-735ms — random pauses that had nothing to do with
 * what she said. Here each chunk is decoded, its silence measured and skipped, and the next chunk
 * is SCHEDULED on the audio clock with the pause the backend says belongs there (a real pause
 * between paragraphs, effectively none mid-sentence).
 *
 * Falls back to element playback when Web Audio is unavailable — Listen still works, just with the
 * engine's own silence at the seams.
 */

const SILENCE_THRESHOLD_DB = -50;

let active = {
  ctx: null, sources: [], gain: null, stale: false,
  // WHO IS SPEAKING, AND WHICH RESPONSE (Founder-authorized 2026-10-08). The element used to be a
  // per-mount ref in Message, which was only correct while Message stayed mounted — so when the
  // element became the canonical surface, closing Message paused real speech and broke the June
  // 2026 ruling that she keeps talking while the member moves around Arvexis. The player, the
  // identity of what it is speaking and the request that feeds it now live here, with the
  // generation counter that was already here.
  player: null, messageKey: null, surface: null, abort: null
};

// WHO IS SPEAKING NOW — the player lives here, so the "am I still the current playback?" counter
// lives here too.
//
// It used to live in the Message component as a per-mount ref, which was only ever correct while
// Message stayed mounted. Playback does not: the context and the scheduled sources are
// module-level and outlive any mount. So after the Founder ruling that Read Aloud keeps playing
// when the member moves to another part of Arvexis, a returning mount's counter no longer had any
// relationship to the sequence still running, and two playbacks could have scheduled into the same
// output at once. One counter, in the same place as the player, is what makes "no two Read Aloud
// responses play at the same time" true across navigation as well as within it.
let generation = 0;

/** The sequence that holds this number is the current one. */
export const playbackGeneration = () => generation;

/** Explicit stop, or an explicit new playback: what is scheduled stops and every older sequence
 *  learns it is no longer current. */
export const invalidatePlayback = () => {
  generation += 1;
  stopScheduled();
  stopOwnedPlayer();
  return generation;
};

// ONE ARVEXIS SPOKEN-MEDIA SESSION AT A TIME (Founder ruling, 2026-10-08).
//
// Message Read Aloud and a Dream reflection are the same voice; two of them at once is never
// something a member asked for. So the rule lives in one place — whoever is about to speak claims
// the session, and claiming it stops whatever held it. No surface needs to remember to stop every
// other surface.

const stopOwnedPlayer = () => {
  if (active.abort) {
    try { active.abort.abort(); } catch (e) { /* already settled */ }
    active.abort = null;
  }
  if (active.player) {
    try { active.player.pause(); } catch (e) { /* already stopped */ }
    active.player = null;
  }
  active.messageKey = null;
  active.surface = null;
};

/** Take the spoken-media session. Whatever held it stops, here, not in the caller. */
export const claimSpokenSession = ({ player = null, messageKey = null, surface = null, abort = null } = {}) => {
  invalidatePlayback();
  active.player = player;
  active.messageKey = messageKey;
  active.surface = surface;
  active.abort = abort;
  return generation;
};

/** Stop whatever is speaking. The explicit Stop, Talk opening, dictation, logout. */
export const stopPlayback = () => invalidatePlayback();

/** What is speaking right now — enough for a returning mount to show the truth, nothing more.
 *  A session is "playing" while it is held: between two chunks the element is briefly paused,
 *  and a member looking at the screen in that moment is still being spoken to. */
export const currentPlayback = () => ({
  playing: Boolean(active.player || active.sources.length),
  messageKey: active.messageKey,
  surface: active.surface,
  generation
});

// ROUTE CHANGES (R2, Founder-authorized) — a Bluetooth car, a headset, a dock.
//
// iOS interrupts the page's audio session on a route change, and an AudioContext that was bound to
// the old route can report a perfectly healthy state while rendering into nothing. Read Aloud went
// silent with no error for the rest of the tab's life, because the context was a singleton that
// was never rebuilt. So the route change is observed and the context is marked stale; the next
// explicit tap builds a fresh one.
//
// Marking only. A route change never interrupts a playback in progress (that is R3, held), never
// touches the microphone, and never declares an audio session (R4, held).
let routeWatcherAttached = false;

const watchRouteChanges = () => {
  if (routeWatcherAttached) return;
  try {
    const md = navigator.mediaDevices;
    if (!md || !md.addEventListener || !('ondevicechange' in md)) return;
    md.addEventListener('devicechange', () => { active.stale = true; });
    routeWatcherAttached = true;
  } catch (e) { /* no route signal on this platform: R1's state test still applies */ }
};

// R1 (Founder-authorized) — `interrupted` is not `closed`, and the old code treated anything that
// was not `closed` as reusable. That is the whole silence-after-Bluetooth bug: the context was
// kept forever and `resume()` was only ever attempted on `suspended`.
// `running` and `suspended` are genuinely reusable — a `suspended` context resumes inside the tap.
// Anything else, and anything marked stale by a route change, is thrown away and rebuilt.
const isReusable = (ctx) =>
  !active.stale && (ctx.state === 'running' || ctx.state === 'suspended');

export const unlockAudioContext = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    watchRouteChanges();
    let ctx = active.ctx && active.ctx.state !== 'closed' ? active.ctx : null;
    if (ctx && !isReusable(ctx)) {
      // Closed before the replacement is built: iOS allows only a handful of live contexts, and
      // until now none were ever closed at all.
      stopScheduled();
      try { ctx.close(); } catch (e) { /* already going away */ }
      ctx = null;
    }
    if (!ctx) ctx = new Ctx();
    active.stale = false;
    if (ctx.state === 'suspended') {
      try { ctx.resume(); } catch (e) { /* the tap still gets a context */ }
    }
    // A one-sample buffer started inside the tap is what unlocks audio on iOS Safari
    const src = ctx.createBufferSource();
    src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    src.connect(ctx.destination);
    src.start(0);
    active.ctx = ctx;
    return ctx;
  } catch (e) {
    return null;
  }
};

export const stopScheduled = () => {
  active.sources.forEach((s) => {
    try { s.onended = null; s.stop(); } catch (e) { /* already finished */ }
  });
  active.sources = [];
};

const base64ToArrayBuffer = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const decodeAudio = (ctx, b64) =>
  new Promise((resolve, reject) => {
    const result = ctx.decodeAudioData(base64ToArrayBuffer(b64), resolve, reject);
    if (result && typeof result.then === 'function') result.then(resolve, reject);
  });

/** Where the speech actually starts and ends inside the clip. */
const speechBounds = (buffer) => {
  const data = buffer.getChannelData(0);
  const threshold = 10 ** (SILENCE_THRESHOLD_DB / 20);
  const win = Math.max(1, Math.floor(buffer.sampleRate * 0.01));
  let startSample = 0;
  let endSample = data.length;

  for (let i = 0; i < data.length; i += win) {
    let peak = 0;
    const stop = Math.min(i + win, data.length);
    for (let j = i; j < stop; j += 1) peak = Math.max(peak, Math.abs(data[j]));
    if (peak > threshold) { startSample = i; break; }
  }
  for (let i = data.length - win; i >= 0; i -= win) {
    let peak = 0;
    const stop = Math.min(i + win, data.length);
    for (let j = i; j < stop; j += 1) peak = Math.max(peak, Math.abs(data[j]));
    if (peak > threshold) { endSample = stop; break; }
  }

  const sr = buffer.sampleRate;
  const offset = Math.max(0, startSample / sr - 0.012);
  const duration = Math.max(0.05, (endSample - startSample) / sr + 0.024);
  return { offset, duration };
};

/**
 * Play the chunks with scheduled pauses.
 * getAudio(i) must return a cached promise of base64 mp3 so prefetching costs nothing.
 */
export const playScheduledChunks = async ({ ctx, chunks, getAudio, volume, isAlive, onFirstAudio }) => {
  stopScheduled();
  const gain = ctx.createGain();
  gain.gain.value = volume;
  gain.connect(ctx.destination);
  active.gain = gain;

  let nextTime = 0;
  let underruns = 0;
  let lastSource = null;
  let played = 0;
  let failure = null;

  for (let i = 0; i < chunks.length; i += 1) {
    // L1-A — A LATE CHUNK MUST NOT KILL AUDIO THAT IS ALREADY PLAYING.
    // By the time chunk i is fetched, chunks 0..i-1 are already SCHEDULED on the audio clock and
    // audible. Letting this throw sent it past them into speakText's catch, which called
    // stopListen() -> stopScheduled() and cut her off mid-word. So the sequence ends here, the
    // tail that is already scheduled is allowed to finish at its own sentence boundary, and the
    // failure is carried back so it is still reported honestly.
    let b64;
    let buffer;
    try {
      b64 = await getAudio(i);
      if (!isAlive()) return { mode: 'webaudio', underruns, cancelled: true };
      buffer = await decodeAudio(ctx, b64);
    } catch (error) {
      failure = {
        message: error?.message || 'chunk_failed',
        httpStatus: error?.httpStatus,
        listenRefreshesOn: error?.listenRefreshesOn,
        chunkIndex: i,
        aborted: error?.name === 'AbortError'
      };
      break;
    }
    if (!isAlive()) return { mode: 'webaudio', underruns, cancelled: true };

    const { offset, duration } = speechBounds(buffer);
    const pause = (chunks[i].pause_before_ms || 0) / 1000;
    let startAt = i === 0 ? ctx.currentTime + 0.06 : nextTime + pause;
    if (startAt < ctx.currentTime) {
      // The next chunk arrived later than the previous one finished — an audible gap happened
      underruns += 1;
      startAt = ctx.currentTime + 0.02;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gain);
    source.start(startAt, offset, duration);
    active.sources.push(source);
    lastSource = source;
    nextTime = startAt + duration;
    played += 1;

    if (i === 0 && onFirstAudio) onFirstAudio();

    // Generate the next chunk while this one plays
    if (i + 1 < chunks.length) getAudio(i + 1).catch(() => {});
  }

  // Resolve when the final chunk has actually finished
  if (lastSource) {
    await new Promise((resolve) => {
      lastSource.onended = resolve;
      const remaining = Math.max(0, nextTime - ctx.currentTime) * 1000 + 250;
      setTimeout(resolve, remaining);
    });
  }
  return { mode: 'webaudio', underruns, cancelled: !isAlive(), played, failure };
};

/** Element playback: one persistent media element for the whole sequence.
 *
 * CANONICAL for Message Read Aloud since the Founder's physical A/B (2026-10-08). The surface
 * matters physically on a phone, in two ways:
 *
 *   - iOS treats Web Audio output as the ambient/ringer session, which the hardware Silent
 *     switch mutes. A playing media element is media playback, which it does not — confirmed
 *     audible on device with Silent ON.
 *   - One element owns decode, buffering and the output route for the whole sequence, the way
 *     Talk's single element already does, instead of N separate sources started on a clock —
 *     confirmed clean over car Bluetooth, where the scheduled path broke up.
 *
 * NOTHING about synthesis changes or has changed: same backend, same engine, same voice, same
 * chunk plan. This function decides only how the returned audio is rendered.
 */
export const playSequentialChunks = async ({ player, chunks, getAudio, isAlive, onFirstAudio }) => {
  let played = 0;
  let failure = null;

  for (let i = 0; i < chunks.length; i += 1) {
    // L1-A — same rule on the element path: what has already been spoken is kept, and the
    // sequence ends at this boundary instead of throwing away the whole playback.
    try {
      const b64 = await getAudio(i);
      if (!isAlive()) return { mode: 'element', cancelled: true, played, failure: null };
      player.src = `data:audio/mp3;base64,${b64}`;
      if (i === 0 && onFirstAudio) onFirstAudio();
      if (i + 1 < chunks.length) getAudio(i + 1).catch(() => {});
      await new Promise((resolve, reject) => {
        player.onended = resolve;
        // An explicit stop pauses the element. `ended` never fires for it, so without this the
        // sequence would wait on a promise that can never settle for the life of the tab.
        player.onpause = () => { if (!isAlive()) resolve(); };
        player.onerror = () => reject(new Error(`playback_error_chunk_${i}`));
        player.play().then(() => {}).catch(reject);
      });
      player.onended = null;
      player.onpause = null;
      player.onerror = null;
    } catch (error) {
      player.onended = null;
      player.onpause = null;
      player.onerror = null;
      failure = {
        message: error?.message || 'chunk_failed',
        httpStatus: error?.httpStatus,
        listenRefreshesOn: error?.listenRefreshesOn,
        chunkIndex: i,
        aborted: error?.name === 'AbortError'
      };
      break;
    }
    played += 1;
    if (!isAlive()) return { mode: 'element', cancelled: true, played, failure: null };
  }
  return { mode: 'element', cancelled: false, played, failure };
};

// ═══════════════════════════════════════════════════════════════════════════════
// WHICH SURFACE PLAYS HER VOICE — SETTLED (Founder physical A/B, 2026-10-08)
//
// The single persistent media element above is canonical. Physical testing on iPhone found it
// audible with the hardware Silent switch ON and clean over car Bluetooth, neither of which the
// scheduled Web Audio path managed. The temporary arm selector has been retired; Message Read
// Aloud now has exactly one playback surface.
//
// `playScheduledChunks` stays in this module for Dreams, which still plays its reflections
// through it, and nothing about that surface changed.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * S1 DEV SPIKE — one continuous streaming response, played by the browser.
 *
 * Founder-authorized DEV-only physical test, Message Read Aloud only. Nothing above this line
 * changes: the Web Audio scheduled path and the element path are the default and the fallback.
 * Here the element owns decode, jitter buffering, scheduling and the output route — the same
 * ownership Talk already has — because there are no chunks to join, no base64 to walk, no
 * per-chunk silence scan and no absolute-time scheduling on the main thread.
 *
 * Resolves { started, completed, cancelled, failure, events, ttfaMs }. `started: false` means
 * no audio was ever heard, which is the only case the caller may fall back from.
 */
export const playStreamedResponse = ({ player, url, isAlive, onFirstAudio }) =>
  new Promise((resolve) => {
    const events = [];
    const startedAt = Date.now();
    let firstAudioAt = null;
    let settled = false;

    const names = ['playing', 'ended', 'error', 'stalled', 'waiting', 'suspend', 'pause', 'abort'];
    const handlers = {};
    const detach = () => names.forEach((n) => player.removeEventListener(n, handlers[n]));

    const finish = (outcome) => {
      if (settled) return;
      settled = true;
      detach();
      resolve({
        mode: 'stream',
        started: firstAudioAt !== null,
        ttfaMs: firstAudioAt === null ? null : firstAudioAt - startedAt,
        events: events.slice(0, 16),
        ...outcome
      });
    };

    handlers.playing = () => {
      if (firstAudioAt !== null) return;
      firstAudioAt = Date.now();
      if (onFirstAudio) onFirstAudio();
    };
    handlers.ended = () => finish({ completed: true, cancelled: !isAlive(), failure: null });
    handlers.error = () => {
      events.push(`error_${player.error?.code ?? 'unknown'}`);
      finish({
        completed: false,
        cancelled: false,
        failure: { message: `stream_element_error_${player.error?.code ?? 'unknown'}` }
      });
    };
    // An explicit stop pauses the element; that is a cancellation, not a failure.
    handlers.pause = () => {
      if (!isAlive() || player.ended) finish({ completed: !!player.ended, cancelled: true, failure: null });
    };
    handlers.abort = () => finish({ completed: false, cancelled: true, failure: null });
    // Observed-only: interruptions worth reporting, never acted on.
    ['stalled', 'waiting', 'suspend'].forEach((n) => {
      handlers[n] = () => { if (events.length < 16) events.push(n); };
    });

    names.forEach((n) => player.addEventListener(n, handlers[n]));

    try {
      player.src = url;
      player.load();
      const p = player.play();
      if (p && typeof p.catch === 'function') {
        p.catch((error) => {
          if (firstAudioAt !== null) return;
          finish({
            completed: false,
            cancelled: false,
            failure: { message: `stream_play_rejected_${error?.name || 'error'}` }
          });
        });
      }
    } catch (error) {
      finish({ completed: false, cancelled: false, failure: { message: 'stream_src_failed' } });
    }
  });
